// EstimateDetail — /office/estimates/new and /office/estimates/:id.
//
// The form is DocumentEditor (kind='estimate'); linking a job previews what
// GET /api/estimates/job-details/:jobId says will come across, and a save
// sends job_ids plus pull_job_content (#505). The state machine is the
// server's (routes/estimates.js); the buttons here are its transitions.
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FwButton, FwField, FwTextarea, Notice, Pill, SectionLabel, EmptyState } from '../../../ui/fw';
import { useCan, useRole } from '../../../hooks/usePermission';
import { get, post, put, del } from '../../shared/http';
import { fmtDateTime } from '../shared/format';
import { estimatePill } from '../shared/status';
// The from-states live in apps/shared/estimateWorkflow.js, which
// estimateWorkflow.test.js checks line by line against the guards in
// server/routes/estimates.js. Hardcoding them here as well is how a screen
// ends up offering a button the server answers 400 to. `revise` keeps its own
// narrower rule below: the server has no guard on it, but revising a draft is
// just editing it, so it is only offered once an estimate is finished.
import { allows } from '../../shared/estimateWorkflow';
// The upload budget, declared once for every screen that sends photos, and
// checked against server/routes/media.js and deploy.sh by photoBatches.test.js.
import { uploadInBatches, uploadSummary } from '../../shared/photoBatches';
import { downloadBlob, copyText } from '../shared/download';
import { PageHeader, Refused, LoadFailed, Loading, ActionError, ConfirmSheet, PickerFailed } from '../shared/Page';
import { DocumentEditor, DocumentSummary } from '../shared/DocumentEditor';
import { ProvenanceCard } from '../shared/provenance';

const EDITABLE = ['draft', 'pending_approval'];

export default function EstimateDetail() {
  const { id } = useParams();
  const isNew = !id;
  const canRead = useCan('estimates', 'read');
  const canCreate = useCan('estimates', 'create');
  const canUpdate = useCan('estimates', 'update');
  const canDelete = useCan('estimates', 'delete');
  const canApprove = useCan('estimates', 'approve_send');
  const canSend = useCan('estimates', 'send');
  const canAccept = useCan('estimates', 'approve_external');
  const canConvert = useCan('estimates', 'convert');
  const canArchive = useCan('estimates', 'archive');
  const isAdmin = useRole() === 'admin';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const est = useQuery({ queryKey: ['office', 'estimate', id], queryFn: () => get(`/estimates/${id}`), enabled: canRead && !isNew, staleTime: Infinity });
  const clients = useQuery({ queryKey: ['office', 'clients'], queryFn: () => get('/clients?active_only=true'), enabled: canRead });
  const jobs = useQuery({ queryKey: ['office', 'estimates', 'available-jobs'], queryFn: () => get('/estimates/available-jobs'), enabled: canRead && canCreate });

  const [saving, setSaving] = useState(false);
  // Bumped only when a save from the FORM landed. DocumentEditor reloads
  // itself from the server on this and on the document's id — never merely
  // because react-query handed back a new object, which is how attaching a
  // photo used to throw away unsaved line edits (DocumentEditor's reset
  // effect carries the browser measurement).
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sheet, setSheet] = useState(null); // 'send' | 'reject' | 'delete' | 'convert'
  const [reason, setReason] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  // Not a flash and not an ActionError: a partial upload is neither a success
  // nor a request that failed. It is a COUNT of what landed and what did not,
  // worded by apps/shared/photoBatches.js so this screen, InvoiceDetail and
  // the Field one cannot describe the same failure differently.
  const [photoNote, setPhotoNote] = useState(null);
  const [photoProgress, setPhotoProgress] = useState(null);
  const [dropPhoto, setDropPhoto] = useState(null);

  const doc = isNew ? null : est.data;
  const status = doc ? (doc.estimate_status || 'draft') : null;
  const clientRecord = useMemo(() => {
    if (!doc || !doc.client_id || !clients.data) return null;
    return clients.data.find((c) => Number(c.id) === Number(doc.client_id)) || null;
  }, [doc, clients.data]);
  // Client record first, snapshot second — the order the SERVER resolves in
  // (server/utils/invoiceContact.js, and routes/estimates.js at the send).
  // This was the other way round, so an estimate whose snapshot held a stale
  // address named that address in the confirm sheet while the send used the
  // corrected one. A confirm sheet that names the wrong recipient is a worse
  // failure than one that names none.
  const recipient = (clientRecord && clientRecord.email) || (doc && doc.client_email) || '';

  if (!canRead) return <Refused what="estimates" />;
  if (isNew && !canCreate) return <Refused what="creating estimates" />;
  if (!isNew && est.isPending) return <><PageHeader title="Estimate" /><Loading lines={8} /></>;
  if (!isNew && est.isError) return <><PageHeader title="Estimate" /><LoadFailed error={est.error} onRetry={() => est.refetch()} what="this estimate" /></>;

  const canEdit = isNew ? canCreate : (canUpdate && EDITABLE.includes(status));
  /**
   * Attaching photos to an estimate — the same two requests as InvoiceDetail.
   *
   *   POST /media/upload      multipart, field `files` (media.js:603)
   *   PUT  /estimates/:id     `photos` → invoice_photos, invoice_item_id NULL
   *
   * That second field is new: only POST accepted `photos` before, so an
   * estimate could be CREATED with before-photos and never gain one. A lead who
   * scoped a job and went back for the shot that explains the price had nowhere
   * to put it.
   *
   * The PUT REPLACES the gallery, so every write sends the whole list with the
   * captions and media_id the existing rows already had. Sending only the new
   * one deletes the rest, and a before-photo removed from an estimate the
   * client can open is not recoverable from the app.
   *
   * Nothing else goes in the body. Totals recompute only when `items` is
   * non-empty or discount/tax is present (`update`, server/routes/estimates/
   * edit.js), so a
   * photos-only save reaches neither — and because it writes no column, it also
   * does not trip the "editing a pending_approval estimate kicks it back to
   * draft" rule. Both are asserted server-side.
   */
  const writePhotos = async (next) => {
    await put(`/estimates/${id}`, {
      photos: next.map((p) => ({
        media_id: p.media_id || null,
        file_path: p.file_path,
        caption: p.caption || '',
      })),
    });
    await refresh();
  };

  /**
   * A selection of any size, sent as however many requests it takes.
   *
   * This built ONE FormData out of whatever the picker returned. The binding
   * limit was never the file count — it is nginx's `client_max_body_size 50M`
   * (deploy.sh:670, :747), enforced before Express sees a byte and unclearable
   * by any server-side count at 25MB a file. The numbers and the arithmetic
   * are in ../../shared/photoBatches.js; the same three points that make this
   * different from the Field path apply here as they do on InvoiceDetail:
   *
   *   · `uploadInBatches` NEVER THROWS — the failure is read off `out`, so a
   *     partial upload can never come out as "30 photos attached."
   *   · whatever landed is LINKED even when a later batch was refused. An
   *     estimate referencing none of the bytes already on disk is a lead sent
   *     back to a property for photographs that are already uploaded.
   *   · ONE PUT after every batch. It REPLACES the gallery, so a PUT per batch
   *     built from a `doc` one refetch behind deletes the earlier batches.
   *
   * Still `{ photos }` and nothing else, whichever it took: that is what keeps
   * estimates.js from recomputing a total AND from reading the save as an edit
   * that sends a pending_approval estimate back to draft.
   */
  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPhotoBusy(true); setError(null); setFlash(null); setPhotoNote(null);
    setPhotoProgress({ sent: 0, total: files.length });
    try {
      const out = await uploadInBatches(files, (batch) => {
        const fd = new FormData();
        batch.forEach((f) => fd.append('files', f, f.name));
        return post('/media/upload', fd);
      }, { onProgress: setPhotoProgress });

      const added = (out.results || []).flatMap((up) => ((up && up.files) || []).map((f) => ({
        media_id: null, file_path: f.file_path, caption: '',
      })));
      // Said BEFORE the link is attempted, so a refused PUT cannot swallow the
      // count of what did upload.
      const trouble = uploadSummary(out);
      if (trouble) setPhotoNote(trouble);

      if (added.length) await writePhotos([...((doc && doc.photos) || []), ...added]);
      else await refresh();

      if (!trouble) setFlash(added.length === 1 ? 'Photo attached.' : `${added.length} photos attached.`);
    } catch (e) {
      // Only the PUT reaches here. The bytes are stored and this estimate
      // references none of them, so the gallery is refetched anyway.
      setError(e);
      try { await refresh(); } catch (_) { /* the error above is the one to read */ }
    } finally { setPhotoBusy(false); setPhotoProgress(null); }
  };

  const removePhoto = async () => {
    const target = dropPhoto;
    if (!target) return;
    setPhotoBusy(true); setError(null); setFlash(null);
    try {
      await writePhotos(((doc && doc.photos) || []).filter((p) => p !== target));
      setDropPhoto(null);
      setFlash('Photo removed from this estimate.');
    } catch (e) { setError(e); } finally { setPhotoBusy(false); }
  };

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['office', 'estimate', id] });
    await qc.invalidateQueries({ queryKey: ['office', 'estimates'] });
  };

  const save = async (payload) => {
    setSaving(true); setError(null); setFlash(null);
    try {
      if (isNew) {
        const created = await post('/estimates', payload);
        await qc.invalidateQueries({ queryKey: ['office', 'estimates'] });
        navigate(`/estimates/${created.id}`, { replace: true });
      } else {
        await put(`/estimates/${id}`, payload);
        await refresh();
        setSavedAt((n) => n + 1);
        setFlash('Saved. The totals below are what the server calculated.');
      }
    } catch (e) { setError(e); } finally { setSaving(false); }
  };

  const run = async (name, fn) => {
    setBusy(name); setError(null); setFlash(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const transition = (name, path, body, done) => run(name, async () => {
    const r = await post(`/estimates/${id}/${path}`, body);
    setSheet(null);
    await refresh();
    if (done) done(r); else setFlash(`${name.charAt(0).toUpperCase()}${name.slice(1)} done.`);
  });
  const previewJob = (jobId) => get(`/estimates/job-details/${jobId}`);
  const copyLink = () => run('copy', async () => {
    const url = `${window.location.origin}/estimate/${doc.access_token}`;
    const ok = await copyText(url);
    setFlash(ok ? `Copied ${url}` : `Could not copy — the link is ${url}`);
  });
  const downloadZip = () => run('zip', async () => { await downloadBlob(`/estimates/${id}/photos.zip`, `${doc.invoice_number || 'estimate'}-photos.zip`); });
  const remove = () => run('delete', async () => { await del(`/estimates/${id}`); setSheet(null); await qc.invalidateQueries({ queryKey: ['office', 'estimates'] }); navigate('/estimates', { replace: true }); });

  // "Uploading 12 of 30…" once there is more than one photo in flight; a single
  // one does not need a count. Same shape as JobPanels' uploadLabel.
  const attachLabel = photoProgress && photoProgress.total > 1
    ? `Uploading ${photoProgress.sent} of ${photoProgress.total}…`
    : 'Attaching…';

  const pill = doc ? estimatePill(doc) : null;
  const title = isNew ? 'New estimate' : (doc.invoice_number || `Estimate #${id}`);
  const meta = doc ? [
    doc.title,
    doc.client_name,
    doc.sent_at ? `Sent ${fmtDateTime(doc.sent_at)}` : null,
    doc.expires_at && status === 'sent' ? `Valid until ${fmtDateTime(doc.expires_at)}` : null,
    doc.accepted_at ? `Accepted ${fmtDateTime(doc.accepted_at)}${doc.accepted_by_name ? ` by ${doc.accepted_by_name}` : ' by the client'}` : null,
    doc.declined_at ? `Declined ${fmtDateTime(doc.declined_at)}` : null,
  ].filter(Boolean).join(' · ') : 'Fill in the client and the lines, then create it to get totals.';

  const actions = doc ? (
    <>
      {allows('submit', status) && canUpdate ? <FwButton variant="primary" icon="upload" onClick={() => transition('submit', 'submit')} loading={busy === 'submit'} disabled={!!busy && busy !== 'submit'}>Submit for approval</FwButton> : null}
      {allows('approve', status) && canApprove ? <FwButton variant="go" icon="check" onClick={() => transition('approve', 'approve')} loading={busy === 'approve'} disabled={!!busy && busy !== 'approve'}>Approve</FwButton> : null}
      {allows('reject', status) && canApprove ? <FwButton variant="ghost" icon="arrowLeft" onClick={() => { setReason(''); setSheet('reject'); }} disabled={!!busy}>Reject</FwButton> : null}
      {allows('send', status) && canSend ? <FwButton variant="primary" icon="mail" onClick={() => setSheet('send')} disabled={!!busy}>Send to client</FwButton> : null}
      {allows('recall', status) && canApprove ? <FwButton variant="ghost" icon="refresh" onClick={() => transition('recall', 'recall')} loading={busy === 'recall'} disabled={!!busy && busy !== 'recall'}>Recall</FwButton> : null}
      {allows('accept-internal', status) && canAccept ? <FwButton variant="go" icon="check" onClick={() => transition('accept', 'accept-internal', undefined, () => setFlash('Recorded the client\'s acceptance.'))} loading={busy === 'accept'} disabled={!!busy && busy !== 'accept'}>Client accepted</FwButton> : null}
      {allows('convert', status) && canConvert && !doc.converted_invoice_id ? <FwButton variant="primary" icon="billing" onClick={() => setSheet('convert')} disabled={!!busy}>Convert to invoice</FwButton> : null}
      {['accepted', 'declined', 'expired'].includes(status) && canCreate ? <FwButton variant="ghost" icon="edit" onClick={() => transition('revise', 'revise', undefined, (r) => navigate(`/estimates/${r.id}`))} loading={busy === 'revise'} disabled={!!busy && busy !== 'revise'}>Revise</FwButton> : null}
      {doc.access_token ? <FwButton variant="ghost" icon="link" onClick={copyLink} loading={busy === 'copy'}>Copy public link</FwButton> : null}
      {doc.photos && doc.photos.length ? <FwButton variant="ghost" icon="download" onClick={downloadZip} loading={busy === 'zip'}>Download photos</FwButton> : null}
      {canArchive ? (doc.archived
        ? <FwButton variant="quiet" icon="refresh" onClick={() => transition('unarchive', 'unarchive')} loading={busy === 'unarchive'}>Unarchive</FwButton>
        : <FwButton variant="quiet" icon="document" onClick={() => transition('archive', 'archive')} loading={busy === 'archive'}>Archive</FwButton>) : null}
      {canDelete && (EDITABLE.includes(status) || isAdmin) ? <FwButton variant="stop" icon="trash" onClick={() => setSheet('delete')} disabled={!!busy}>Delete</FwButton> : null}
    </>
  ) : <FwButton variant="ghost" onClick={() => navigate('/estimates')}>Back to estimates</FwButton>;

  return (
    <>
      <PageHeader title={<>{title}{pill ? <> <Pill tone={pill.tone} data-status={status}>{pill.label}</Pill></> : null}</>} meta={meta} actions={actions} />
      {/* What the quote is for, before anything else on the page. Measured at
          320px before this landed: "Total $4,200.00" was at y=2893 of a
          3626px page — the figure a client rings up about, three screenfuls
          below the fold. Nothing is computed here; see DocumentSummary. */}
      <DocumentSummary doc={doc} kind="estimate" />
      {/* `flash` is a string, or { text, tone } when the outcome is not a
          success. A send that reached nobody is not an "ok" — it used to be
          announced in the same green notice as a delivered one. */}
      {flash ? <Notice tone={flash.tone || 'ok'} onDismiss={() => setFlash(null)} data-flash={flash.tone || 'ok'}>{flash.text || flash}</Notice> : null}
      <ActionError error={error} onDismiss={() => setError(null)} />
      {/* A partial upload, named. Never green: some of what was picked is not
          on this estimate, and the sentence says how many and why. */}
      {photoNote ? <Notice tone="warn" onDismiss={() => setPhotoNote(null)} data-photo-note>{photoNote}</Notice> : null}
      {doc && !canEdit && canUpdate ? (
        <Notice tone="info">
          {status === 'sent' ? 'This estimate has been sent. Recall it to edit.' : status === 'approved' ? 'Approved and ready to send. Editing would need it re-approved — recall is only available after sending; ask an admin if the content must change.' : `This estimate is ${status.replace('_', ' ')} and read-only. Use Revise to make a new draft from it.`}
        </Notice>
      ) : null}
      {doc && doc.converted_invoice_id ? <Notice tone="ok" icon="billing">Converted to invoice {doc.converted_invoice_number || `#${doc.converted_invoice_id}`}. <FwButton variant="quiet" onClick={() => navigate(`/invoices/${doc.converted_invoice_id}`)}>Open it</FwButton></Notice> : null}
      {doc && doc.parent_estimate_id ? <Notice tone="info">This is a revision of estimate #{doc.parent_estimate_id}.</Notice> : null}

      {/* See InvoiceDetail for the reasoning: an empty <select> inside
          DocumentEditor is a claim ("there are none"), and `… .data || []`
          makes a refusal look identical to one. Gated on `canEdit` because a
          read-only estimate disables the client <select> and renders no job
          picker, and warning about a control that is not there would be its
          own false claim. */}
      {canEdit && clients.isError ? (
        <PickerFailed
          what="the client list"
          error={clients.error}
          onRetry={() => clients.refetch()}
          consequence="The Client dropdown below is empty because the list was refused, not because the client is missing — check before typing the billing details in by hand, or you will create a second record for a client who is already on file."
        />
      ) : null}
      {canEdit && jobs.isError ? (
        <PickerFailed
          what="the jobs that can be estimated"
          error={jobs.error}
          onRetry={() => jobs.refetch()}
          consequence="Jobs already linked to this estimate are still listed below; the picker just cannot offer new ones until this loads."
        />
      ) : null}

      <DocumentEditor
        kind="estimate"
        doc={doc}
        isNew={isNew}
        clients={clients.data || []}
        availableJobs={jobs.data || []}
        previewJob={previewJob}
        canEdit={canEdit}
        saving={saving}
        savedAt={savedAt}
        onSave={save}
      />

      {doc ? (
        <section className="of-card">
          <div className="of-section-head">
            <SectionLabel>Photos</SectionLabel>
            {canEdit && !isNew ? (
              <label className="of-file">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={photoBusy}
                  aria-label="Attach photos to this estimate"
                  data-action="add-photos"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
                />
                {/* Thirty photos over an office connection is several requests
                    and a real wait. A control that says only "Attaching…" for
                    that long is one somebody taps again or walks away from, so
                    it counts — the same sentence JobPanels shows. */}
                <span>{photoBusy ? attachLabel : 'Attach photos'}</span>
              </label>
            ) : null}
          </div>
          {doc.photos && doc.photos.length ? (
            <div className="of-gallery">
              {doc.photos.map((p) => (
                <figure className="of-gallery__item" key={p.id || p.media_id || p.file_path}>
                  <img src={p.file_path} alt={p.caption || 'Estimate photo'} loading="lazy" />
                  {p.caption ? <figcaption>{p.caption}</figcaption> : null}
                  {canEdit ? (
                    <FwButton
                      variant="ghost"
                      icon="trash"
                      disabled={photoBusy}
                      data-action="remove-photo"
                      onClick={() => setDropPhoto(p)}
                    >Remove</FwButton>
                  ) : null}
                </figure>
              ))}
            </div>
          ) : (
            <EmptyState icon="image" title="No photos on this estimate">
              {canEdit && !isNew
                ? 'Attach photos, or tick "Bring the job\'s photos and work onto the estimate" when linking a job.'
                : 'Tick "Bring the job\'s photos and work onto the estimate" when linking a job to carry its photos across.'}
            </EmptyState>
          )}
        </section>
      ) : null}

      {/* The audit line — docs/PARITY_2026-09-08.md §14. Who raised it, who
          approved it, who accepted it, drawn from names the route has always
          sent and no screen read. It sits AFTER the document because it answers
          a question asked about the estimate rather than one asked of it, and
          the shared component keeps the wording identical to InvoiceDetail's. */}
      <ProvenanceCard doc={doc} />

      {/* Removing a photo is a delete on the server — the gallery is REPLACED by
          whatever this screen sends — so it is confirmed rather than one
          mis-tap away. */}
      <ConfirmSheet
        open={!!dropPhoto}
        title="Remove this photo?"
        onClose={() => setDropPhoto(null)}
        onConfirm={removePhoto}
        confirmLabel="Remove"
        tone="stop"
        busy={photoBusy}
      >
        <p>It comes off this estimate, including the copy the client can open. Photos on the linked job are not touched.</p>
      </ConfirmSheet>

      {/* The flash reports what the SERVER says happened, not what we hoped.
          This read `r.emailed ? 'Sent to …' : 'No email address was on file'`,
          and `emailed` was true whatever the mail provider answered — so an
          unconfigured mailer, or one that refused the message, both reported
          "Sent to bob@example.com." in green. The server now answers
          `emailed`, `sent_to` and a `delivery` sentence; the failure branch is
          no longer allowed to guess at the reason either, because "no address
          on file" was only ever one of three. */}
      <ConfirmSheet open={sheet === 'send'} title="Send this estimate?" onClose={() => setSheet(null)} onConfirm={() => transition('send', 'send', undefined, (r) => setFlash(r && r.emailed
        ? `Sent to ${r.sent_to || recipient}.`
        : { tone: 'warn', text: `Marked sent, but nothing was emailed — ${(r && r.delivery) || 'the send did not report a reason'}. Copy the public link and send it yourself.` }))} confirmLabel="Send" busy={busy === 'send'}>
        {recipient
          ? <p>It will be emailed to <strong data-recipient={recipient}>{recipient}</strong> with a link to review, accept or decline. It stays valid for 30 days unless a date was set.</p>
          : <Notice tone="warn">No email address is on file. The estimate will be marked sent, but nothing will be emailed — you can copy the public link afterwards.</Notice>}
      </ConfirmSheet>
      <ConfirmSheet open={sheet === 'reject'} title="Send back to draft?" onClose={() => setSheet(null)} onConfirm={() => transition('reject', 'reject', { reason })} confirmLabel="Reject" tone="stop" busy={busy === 'reject'}>
        <FwField label="Note for the author" hint="Recorded in the activity log.">
          <FwTextarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-field="reject_reason" />
        </FwField>
      </ConfirmSheet>
      <ConfirmSheet open={sheet === 'convert'} title="Convert to an invoice?" onClose={() => setSheet(null)} onConfirm={() => transition('convert', 'convert', undefined, (r) => navigate(`/invoices/${r.id}`))} confirmLabel="Convert" busy={busy === 'convert'}>
        <p>A new draft invoice is created with this estimate's lines, photos and linked jobs, using the client's current contact details. The estimate stays as the record of what was agreed.</p>
      </ConfirmSheet>
      <ConfirmSheet open={sheet === 'delete'} title="Delete this estimate?" onClose={() => setSheet(null)} onConfirm={remove} confirmLabel="Delete" tone="stop" busy={busy === 'delete'}>
        <p>{doc ? doc.invoice_number : 'This estimate'} will be removed. Archiving keeps the record; deleting is for mistakes.</p>
      </ConfirmSheet>
    </>
  );
}
